export class W3Counter {
  counters: NodeListOf<HTMLElement> | null;
  observer!: IntersectionObserver;

  constructor() {
    this.counters = document.querySelectorAll(".counter__number");
    this.setupObserver();
  }

  /**
   * setupObserver - Setup the IntersectionObserver to animate the counters
   */
  private setupObserver(): void {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // trigger when at least 10% of the element is visible
    };

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          const targetValue = parseFloat(target.dataset.counter);
          const targetLanguage = target.dataset.language;

          this.setInitialWidth(entry.target as HTMLElement, targetValue);
          this.animateCounter(
            entry.target as HTMLElement,
            0,
            targetValue,
            2000,
            targetLanguage,
          );
          observer.unobserve(entry.target); // Stop observing the element after animation starts
        }
      });
    }, options);

    this.counters.forEach((counter) => {
      this.observer.observe(counter);
    });
  }

  /**
   * setInitialWidth - Set the initial width of the counter element
   * @param {HTMLElement} element - The counter element
   * @param {number} value - The value of the counter
   */
  private setInitialWidth(element: HTMLElement, value: number): void {
    const font = this.getDynamicFont();
    const width = this.getTextWidth(value.toLocaleString("de-DE"), font);
    element.style.minWidth = `${width}px`; // Set min-width to fit the largest number
    element.style.display = "flex";
    element.style.justifyContent = "center";
  }

  /**
   * getDynamicFont - Get the dynamic font size based on the viewport width
   * @returns {string} The font size in rem
   */
  private getDynamicFont(): string {
    const vw = window.innerWidth / 16;
    const minFontSize = 2; // Minimum font size in rem
    const maxFontSize = 7; // Maximum font size in rem
    const baseMinWidth = 31.25; // Minimum viewport width in rem (500px)
    const baseMaxWidth = 97; // Maximum viewport width in rem (1552px)
    let fontSize = minFontSize;

    if (vw >= baseMaxWidth) {
      fontSize = maxFontSize;
    } else if (vw > baseMinWidth) {
      fontSize =
        minFontSize +
        (maxFontSize - minFontSize) *
          ((vw - baseMinWidth) / (baseMaxWidth - baseMinWidth));
    }

    return `500 ${fontSize}rem 'BagossExtended', sans-serif`;
  }

  /**
   * getTextWidth - Get the width of the text
   * @param {string} text - The text to measure
   * @param {string} font - The font size and family
   * @returns {number} The width of the text
   */
  private getTextWidth(text: string, font: string): number {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return 0;
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  /**
   * animateCounter - Animate the counter from the start to the end value
   * @param {HTMLElement} element - The counter element
   * @param {number} start - The start value
   * @param {number} end - The end value
   * @param {number} duration - The duration of the animation
   */
  private animateCounter(
    element: HTMLElement,
    start: number,
    end: number,
    duration: number,
    language: string,
  ): void {
    let startTime: number;

    const startAnimation = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      // Calculate the current number with rounding to two decimals
      const currentNumber = parseFloat(
        (start + (end - start) * progress).toFixed(1),
      );

      element.textContent = this.addDelimiter(currentNumber, language);

      if (progress < 1) {
        window.requestAnimationFrame(startAnimation);
      } else {
        // Ensure the final number is formatted correctly and rounded
        element.textContent = this.addDelimiter(
          parseFloat(end.toFixed(2)),
          language,
        );
      }
    };

    window.requestAnimationFrame(startAnimation);
  }

  /**
   * addDelimiter - Add a delimiter to the number for better readability
   * @param {number} number - The number to add the delimiter to
   * @param {string} language - The selected language
   * @returns {string} The formatted number
   */
  private addDelimiter(number: number, language: string): string {
    // Determine the decimal and thousand separators based on the language
    let decimalSeparator = language === "german" ? "," : ".";
    let thousandSeparator = language === "german" ? "." : ",";

    // Convert the number to a string and split it based on the decimal point
    let parts = number.toString().split(".");

    // Format the integer part with the correct thousand separator
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

    // If there is a fractional part, retain it (already correctly formatted)
    // If language is not "german", keep it with a dot as the decimal separator
    if (parts.length > 1) {
      parts[1] = parts[1];
    }

    // Join the parts back together with the correct decimal separator
    return parts.join(decimalSeparator);
  }
}
